import { useEffect } from "react";
import { Navigate, NavLink, Outlet, useNavigate, useOutletContext } from "react-router";

import type { DatasetResource } from "@prequel/import-react";
import { useDataset } from "@prequel/import-react";
import { Button, ButtonStyle, checkTabIsActive, PageHeading, Spinner, Tabs } from "@prequel/shared-components";

import Breadcrumbs from "@/components/Breadcrumbs";
import { useRequiredParam } from "@/layouts/Navigation/useRequiredParam";
import { useToast } from "@/providers/ToastProvider";

export type DatasetContext = {
  dataset: DatasetResource;
  refreshDataset: () => void;
};

export function useDatasetContext(): DatasetContext {
  return useOutletContext<DatasetContext>();
}

const DatasetPage = () => {
  const datasetID = useRequiredParam("datasetID");
  const { addToast } = useToast();
  const { data: dataset, error, isLoading, refetch: refreshDataset } = useDataset(datasetID);

  useEffect(() => {
    if (error) {
      addToast({ kind: "ERROR", message: "Failed to load dataset." });
    }
  }, [error, addToast]);

  if (isLoading) {
    return <Spinner />;
  }

  if (!dataset) {
    return <Navigate to="/import/datasets" replace />;
  }

  return <Outlet context={{ dataset, refreshDataset }} />;
};

export const DatasetDetailsPage = () => {
  const navigate = useNavigate();
  const { dataset, refreshDataset } = useDatasetContext();

  const tabs = [
    <NavLink key="Extracts" to={`/import/datasets/${dataset.id}`} className={checkTabIsActive} end>
      Extracts
    </NavLink>,
    <NavLink key="Details" to={`/import/datasets/${dataset.id}/details`} className={checkTabIsActive} end>
      Details
    </NavLink>,
  ];

  return (
    <div className="max-w-4xl px-7 py-4">
      <PageHeading
        title={dataset.method === "table" ? dataset.table.source_table_name : dataset.id}
        onGoBack={() => navigate("/import/datasets")}
        breadcrumbs={
          <Breadcrumbs dynamicLabel={dataset.method === "table" ? dataset.table.source_table_name : dataset.id} />
        }
      >
        <Button
          text="Edit"
          onClick={() => navigate(`/import/datasets/${dataset.id}/edit`)}
          type={ButtonStyle.SECONDARY}
        />
      </PageHeading>
      <div className="flex-1">
        <div className="relative">
          <div className="pt-0 pb-16">
            <div className="pb-6">
              <Tabs tabs={tabs} />
              <Outlet context={{ dataset, refreshDataset }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatasetPage;
